

import Vue from 'vue'
import Component from "vue-class-component";
import {namespace} from "vuex-class";
import {Prop} from "vue-property-decorator";

import LMModule from "@/components/locker/modules/LMModule.vue";
import LMCreateModuleDialog from "@/components/locker/modules/LMCreateModuleDialog.vue";
import ResponsiveTopMenu from "@/components/shared/ResponsiveTopMenu.vue";
import {PermissionHelper} from "@/helpers/PermissionHelper";
import {EUserPermissions} from "@/model/EUserPermissions";
import {EPermissionActions} from "@/model/EPermissionActions";
import {IAuthUserData} from "@/store/modules/authModule";
import LMCredentialsDialog from "@/components/locker/modules/LMCredentialsDialog.vue";
import LMUploadLogoDialog from "@/components/locker/modules/LMUploadLogoDialog.vue";
import CreateButton from "@/components/shared/CreateButton.vue";
import {IModule} from "@/model/Locker/IModule";
import {ICell, ILocker} from "@/model/Locker/lockerInterfaces";
import {SCREEN_ORIENTATIONS, SCREEN_TYPE_PRESETS} from "@/constants/locker";
import {IOnOffHandlers} from "bonomo/dist";
import {IListOrder} from "@/model/Order/orderInterfaces";
import LockerExternalNameDialog from "@/components/settings/locker/LockerExternalNameDialog.vue";
import {LockerCellActions} from "@/constants/locker";
import {IShortServiceProvider} from "@/model/shortInterfaces";
import SimpleConfirmationDialog from "@/components/shared/SimpleConfirmationDialog.vue";

const authModule = namespace("authModule");

  const LockerStatuses = {
    Deleted: 'lockers.locker.statuses.deleted',
    Online: 'lockers.locker.statuses.online',
    Install: 'lockers.locker.statuses.install',
    Off: 'lockers.locker.statuses.off',
  };

  @Component({
    name: 'LMLocker',
    components: {
      SimpleConfirmationDialog,
      LockerExternalNameDialog,
      LMUploadLogoDialog,
      LMModule,
      LMCreateModuleDialog,
      LMCredentialsDialog,
      CreateButton,
      ResponsiveTopMenu,
    }
  })
  export default class LMLocker extends Vue {

    @Prop()
    protected locker!: ILocker;
    @Prop()
    protected modules!: IModule[];
    @Prop()
    protected lockersOrders!: IListOrder[];
    @Prop()
    protected lockerProviders!: IShortServiceProvider[];

    @authModule.Getter('userData')
    authUser!: IAuthUserData;

    actionList = LockerCellActions;
    action: LockerCellActions = this.actionList.accessible;
    statuses: object = LockerStatuses;
    submitting: boolean = false;
    showRebootConfirmation: boolean = false;
    hideUnused: boolean = true;
    ons: IOnOffHandlers = {};


    // @todo these should use the permission system instead of being hardcoded
    // it might be feasible to make this decision in BE and return in meta data
    protected get canOpenCells() {
      const authUser = this.authUser;
      return PermissionHelper.hasRoleAdministrator(authUser) ||
          PermissionHelper.hasPermission(authUser, EUserPermissions.PERMISSION_INSTALLER, this.locker.community_id) ||
          PermissionHelper.hasPermission(authUser, EUserPermissions.PERMISSION_REMOTE_OPEN_CELLS, this.locker.community_id) ||
          this.canLockerFactory;
    }

    protected get canInstallLocker() {
      const authUser = this.authUser;
      return PermissionHelper.hasPermission(authUser, EUserPermissions.PERMISSION_INSTALLER, this.locker.community_id);
    }

    protected get canLockerFactory() {
      const authUser = this.authUser;
      return PermissionHelper.hasPermission(authUser, EUserPermissions.PERMISSION_LOCKER_FACTORY, 1);
    }

    protected get canInstallLockerOrFactory() {
      return this.canInstallLocker || this.canLockerFactory;
    }

    protected get canUpdateLockerScreen() {
      return this.$canAction(EPermissionActions.ACTION_LOCKERS_UPDATE);
    }

    protected get canBlockCells() {
      return this.canOpenCells ||
        PermissionHelper.hasPermission(this.authUser, EUserPermissions.PERMISSION_LOCKER_OWNER, this.locker.community_id);
    }

    protected get canRebootLocker() {
      return this.canInstallLockerOrFactory ||
        PermissionHelper.hasPermission(this.authUser, EUserPermissions.PERMISSION_LOCKER_OWNER, this.locker.community_id);
    }

    protected  get hasMachineNumber(): boolean {
      return !!this.locker.machine_number && (parseInt(this.locker.machine_number) > 1);
    }

    protected get screenOrientation(): string {
      return (this.locker.screenConfig && this.locker.screenConfig.orientation) || '';
    }

    // orientation that matches the current screen type (big/small) resolution
    protected get defaultOrientation(): string {
      const resolution = this.locker.screenConfig && this.locker.screenConfig.resolution;
      const preset = Object.values(SCREEN_TYPE_PRESETS).find((each) => each.resolution === resolution);
      return preset ? preset.orientation : '';
    }

    protected get orientations(): Record<string, string> {
      const defaultOrientation = this.defaultOrientation;
      const orientations: Record<string, string> = {};
      SCREEN_ORIENTATIONS.forEach((orientation) => {
        const label = this.$t('lockers.install.cells.orientations.' + orientation) as string;
        orientations[orientation] = orientation === defaultOrientation
          ? `${label} ${this.$t('lockers.install.cells.default')}`
          : label;
      });
      return orientations;
    }

    protected get visibleModules(): IModule[] {
      const filtered = this.hideUnused
        ? this.modules.filter((module: IModule) => !module.blocked_column)
        : this.modules;
      return filtered.slice().sort((a: IModule, b: IModule) => {
        const ao = a.column_order;
        const bo = b.column_order;
        if (ao > 0 && bo > 0) return ao - bo;
        if (ao > 0) return -1;
        if (bo > 0) return 1;
        return 0;
      });
    }

    protected isModuleHidden(module: IModule): boolean {
      return !!module.blocked_column;
    }

    protected onSetModuleOrder(payload: { module: IModule, order: number }) {
      this.$store.dispatch('lockerModule/setModuleOrder', {
        lockerId: this.locker.id,
        cellIds: this.moduleCellIds(payload.module),
        order: payload.order,
      });
    }

    protected onHideModule(module: IModule) {
      this.$store.dispatch('lockerModule/hideModule', {
        lockerId: this.locker.id,
        cellIds: this.moduleCellIds(module),
        hidden: !module.blocked_column,
      });
    }

    private moduleCellIds(module: IModule): number[] {
      const ids: number[] = [];
      module.columns.forEach((col: ICell[]) => col.forEach((cell: ICell) => ids.push(cell.id)));
      return ids;
    }

    public created() {
      this.ons = this.$ons({
        'backend.call.begin.lockerUpdated': this.onSubmitting,
        'backend.call.OK.lockerUpdated': this.onSubmittingEnd,
        'backend.call.KO.lockerUpdated': this.onSubmittingEnd,
        'backend.call.begin.cellOpened': this.onSubmitting,
        'backend.call.OK.cellOpened': this.onCellOpenedOK,
        'backend.call.KO.cellOpened': this.onSubmittingEnd,
        'backend.call.begin.orderCollected': this.onSubmitting,
        'backend.call.OK.orderCollected': this.onOrderCollected,
        'backend.call.KO.orderCollected': this.onSubmittingEnd,
        'backend.call.begin.takeScreenShot': this.onSubmitting,
        'backend.call.OK.takeScreenShot': this.onSubmittingEnd,
        'backend.call.KO.takeScreenShot': this.onSubmittingEnd,
        'backend.call.OK.rebootLocker': this.onRebootLockerOK,
        'backend.call.KO.rebootLocker': this.onRebootLockerKO,
        'backend.call.OK.lockerScreenConfigSet': this.onScreenOrientationSaved,
        'backend.call.begin.imageUploaded': this.onSubmitting,
        'backend.call.OK.imageUploaded': this.onSubmittingEnd,
      });
      this.action = this.canOpenCells || this.canLockerFactory ? this.actionList.disable : this.actionList.accessible;
    }

    public beforeDestroy() {
      this.$offs(this.ons);
    }

    protected onBlockButtonClicked(module: IModule) {
      let cellIds: number[] = [];
      module.columns.forEach((eachColumn: ICell[]) => {
        eachColumn.forEach(eachCell => cellIds.push(eachCell.id));
      });
      this.$uiBus.emit('field-editor.show', {
        'title': 'Modul block #',
        'value': module.block,
        'event': 'save-locker-block',
        'data': {
          cells: cellIds,
        }
      });
    }

    protected onChanged() {
      this.$emit('changed');
    }

    protected onCreateModuleClicked() {
      this.$uiBus.emit('l-m-create-module-dialog.show', this.locker);
    }

    protected onMachineNumberClicked() {
      this.$uiBus.emit('field-editor.show', {
        type: 'textfield',
        title: 'lockers.locker.fields.machine_number.title',
        description: this.$t("lockers.locker.fields.machine_number.description"),
        value: this.locker.machine_number,
        event: 'save-machine-number',
      });
    }

    protected onPushClicked() {
      this.$uiBus.emit('l-m-credentials-dialog.show', this.locker);
    }

    protected onStatusClicked() {
      this.$uiBus.emit('field-editor.show', {
        type: 'select',
        title: 'lockers.locker.statuses.title',
        value: this.locker.status,
        values: LockerStatuses,
        event: 'save-locker-status',
      })
    }

    protected onRebootClicked() {
      this.showRebootConfirmation = true;
    }

    protected onScreenOrientationClicked() {
      this.$uiBus.emit('field-editor.show', {
        type: 'select',
        title: 'lockers.install.cells.rotateScreen',
        value: this.screenOrientation,
        values: this.orientations,
        event: 'save-screen-orientation',
      });
    }

    private onScreenOrientationSaved() {
      this.$uiBus.emit('success', 'OK');
    }

    protected toogleHideUnused() {
      this.hideUnused = !this.hideUnused;
    }

    private onSubmitting() {
      this.submitting = true;
    }
    private onSubmittingEnd() {
      this.submitting = false;
      this.$uiBus.emit('success', 'OK');
    }
    private onCellOpenedOK() {
      this.submitting = false;
      this.$uiBus.emit('success', this.$t('lockers.install.cells.openDialog.cellOpened'));
    }
    private onOrderCollected() {
      this.submitting = false;
      this.$uiBus.emit('success', this.$t('lockers.install.cells.openDialog.orderCompletedToast'));
      this.onChanged();
    }

    protected onRebootLocker() {
      this.submitting = true;
      this.$store.dispatch('lockerModule/rebootLocker', {
        lockerId: this.locker.id
      });
    }
    private onRebootLockerOK() {
      this.submitting = false;
      this.showRebootConfirmation = false;
      this.$uiBus.emit('success', 'OK');
    }
    private onRebootLockerKO() {
      this.submitting = false;
      this.$uiBus.emit('alert', 'KO');
    }

    protected toggleAction(nextAction: LockerCellActions) {
      this.action = nextAction;
    }

  }

